import { useEffect, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import {
  DashboardFilterType,
  DashboardMonthlyBidCount,
} from "@/types/dashboard";

import { showToast } from "@/utils/toastUtils";

import { getDashboardBidFilterToString } from "@/consts/Dashboard";

import { GetMonthlyBidCount } from "@/services/Dashboard";

import DashboardCardHeader from "@/components/DashboardCardHeader";

const CHART_KEYS = [
  { key: "contract_count", label: "계약", color: "#f59e0b" },
  { key: "bid_count", label: "투찰", color: "#f9a8d4" },
  { key: "notice_count", label: "공고", color: "#2563eb" },
];

const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="bg-white p-2 rounded shadow text-sm text-black border">
      <div className="font-bold mb-1">{label}</div>
      {[...payload].reverse().map((entry, index) => (
        <div key={index} style={{ color: entry.color }}>
          {CHART_KEYS.find((k) => k.key === entry.dataKey)?.label ||
            CHART_KEYS[index].label}
          : {entry.value.toLocaleString()}
        </div>
      ))}
    </div>
  );
};

type Props = {
  title: string;
  searchReady: boolean;
  filter: DashboardFilterType;
  onSearchComplete: () => void;
};

export default function DashboardStackedBarChart({
  title,
  searchReady,
  filter,
  onSearchComplete,
}: Props) {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [data, setData] = useState<DashboardMonthlyBidCount[]>([]);
  const [visibleKeys, setVisibleKeys] = useState<string[]>(
    CHART_KEYS.map((k) => k.key)
  );
  const topKey = CHART_KEYS.find(({ key }) => visibleKeys.includes(key))?.key;

  const onLoad = async () => {
    try {
      setIsLoading(true);
      const params = getDashboardBidFilterToString(filter);
      const result = await GetMonthlyBidCount(params);
      if (result.data.success) {
        const newData = result.data.pods[0].content.items.map((item) => ({
          notice_count: item.notice_count,
          bid_count: item.bid_count,
          contract_count: item.contract_count,
          month: `${String(item.year).substring(2)}.${String(item.month).padStart(2, "0")}`,
        }));
        setData(newData);
      } else {
        showToast({
          type: "error",
          message: `${title}을(를) 조회하는데 실패했습니다. ${result.data.error.type}`,
        });
      }
    } catch (e) {
      showToast({
        type: "error",
        message: `${title}을(를) 조회하는데 실패했습니다. ${e}`,
      });
    } finally {
      setIsLoading(false);
      onSearchComplete();
    }
  };

  useEffect(() => {
    if (searchReady) {
      onLoad();
    }
  }, [searchReady]);

  const toggleKey = (key: string) => {
    setVisibleKeys((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  return (
    <DashboardCardHeader title={title} isLoading={isLoading}>
      <div className="flex gap-2 mb-2">
        {CHART_KEYS.map(({ key, label, color }) => (
          <div
            key={key}
            className={`flex gap-1.5 items-center text-xs font-semibold cursor-pointer ${
              visibleKeys.includes(key) ? "text-gray-A" : "text-gray-B"
            }`}
            onClick={() => toggleKey(key)}
          >
            <div
              className={`size-2 rounded-full ${
                visibleKeys.includes(key) ? "bg-opacity-100" : "bg-opacity-50"
              }`}
              style={{
                backgroundColor: color,
                opacity: visibleKeys.includes(key) ? 1 : 0.5,
              }}
            ></div>
            {label}
          </div>
        ))}
      </div>
      <ResponsiveContainer width="100%" height="90%">
        <BarChart data={data} margin={{ right: 5, left: 20 }}>
          <CartesianGrid
            strokeDasharray="0"
            vertical={false}
            stroke="#CBCDCE"
          />
          <XAxis
            dataKey="month"
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 14, fontWeight: 700 }}
          />
          <YAxis
            width={30}
            tick={{ fontSize: 14, fontWeight: 700 }}
            domain={[0, "dataMax"]}
            axisLine={false}
            tickLine={false}
          />
          <Tooltip content={<CustomTooltip />} />

          {[...CHART_KEYS].map(({ key, color }) => (
            <Bar
              key={key}
              dataKey={key}
              stackId="a"
              fill={color}
              barSize={10}
              radius={key === topKey ? [6, 6, 0, 0] : 0}
              hide={!visibleKeys.includes(key)}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </DashboardCardHeader>
  );
}
